import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Paper, Snackbar, Alert, Modal, LinearProgress
} from '@mui/material';
import ScrollToTop from '../../../../components/ScrollToTop';
import { toast } from 'react-toastify';
import axios from 'axios';
import {axiosInstance, localAxiosPut } from '../../../../Axios/axios'
import TokenExpirePopup from '../../../../components/TokenExpirePopup';
import { useNavigate } from 'react-router-dom';

// Mock list of banks for the dropdown, including 'Other'
const mockBanks = ['HDFC Bank', 'ICICI Bank', 'State Bank of India', 'Axis Bank', 'Kotak Mahindra Bank', 'Yes Bank', 'Other'];
// Added 'Salary' and set it as default
const accountTypes = ['Salary', 'Savings', 'Current', 'NRE/NRO'];

// COMPACT SubHeading Style
const SubHeading = ({ children }) => (
    <Typography
        variant="h6"
        sx={{
            mt: 1.5,
            mb: 1,
            pb: 0.5,
            borderBottom: '1px solid #ddd',
            fontWeight: 700
        }}
    >
        {children}
    </Typography>
);

const BankingStatutory = ({ userId, initialData, navigationContext, onSaveAndReturn, onUpdateEmployeeSuccess, onFormCompletionChange }) => {

    const defaultFormData = {
        // Banking Details
        bankName: 'Salary',
        otherBankName: '',
        branchName: '',
        accountHolderName: '',
        accountNumber: '',
        accountType: 'Salary',
        ifscCode: '',

        // Statutory Details (India-specific)
        panNumber: '',
        aadhaarNumber: '',
        uanNumber: '',
        esicNumber: '',
        esiEnrolledDate: '',
        pfStartDate: '',
        pfNumber:'',
        gratuityNumber:'',
        passportNumber: '',
        drivingLicenseNumber: '',

        // INSURANCE FIELDS
        insurancePolicyNumber: '',
        insurancePolicyIssuedDate: '',
    };

    const [formData, setFormData] = useState(defaultFormData);
    const [loading, setLoading] = useState(false);
    const Token = localStorage.getItem('accessToken');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [openTokenExpirePopup, setOpenTokenExpirePopup] = useState(false);
    const navigate = useNavigate();
    const [completion, setCompletion] = useState(0);


    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };
    const handleCloseTokenExpirePopup = () => {
        setOpenTokenExpirePopup(false);
        navigate('/');
    };

    // Field mapping function
    const mapInitialDataToForm = (apiData) => {
        if (!apiData) return defaultFormData;

        return {
            // Banking Details
            bankName: apiData.bank_name || 'Salary',
            otherBankName: '',
            branchName: apiData.branch_name || '',
            accountHolderName: apiData.account_holder_name || '',
            accountNumber: apiData.account_number || '',
            accountType: apiData.account_type || 'Salary',
            ifscCode: apiData.ifsc_code || '',

            // Statutory Details
            panNumber: apiData.pan_number || '',
            aadhaarNumber: apiData.aadhaar_number || '',
            uanNumber: apiData.uan_number || '',
            esicNumber: apiData.esic_number || '',
            esiEnrolledDate: apiData.esic_enrollment_date || '',
            pfStartDate: apiData.pf_enrollment_date || '',
            pfNumber: apiData.pf_number || '',
            gratuityNumber:apiData.gratuity_number || '',
            passportNumber: apiData.passport_number || '',
            drivingLicenseNumber: apiData.driving_license_number || '',

            // Insurance Fields
            insurancePolicyNumber: apiData.insurance_policy_number || '',
            insurancePolicyIssuedDate: apiData.policy_issued_date || '',
        };
    };

    // useEffect to handle pre-filling when initialData is provided
    useEffect(() => {
        if (initialData) {
            const mappedData = mapInitialDataToForm(initialData);
            setFormData(mappedData);
        }
    }, [initialData]);

    const calculateProgress = React.useCallback(() => {
        const allFields = [
            'accountHolderName',
            'accountNumber',
            'bankName',
            ...(formData.bankName === 'Other' ? ['otherBankName'] : []),
            'branchName',
            'accountType',
            'ifscCode',
            'panNumber',
            'aadhaarNumber',
            'uanNumber',
            'esicNumber',
            'esiEnrolledDate',
            'pfStartDate',
            'pfNumber',
            'gratuityNumber',
            'passportNumber',
            'drivingLicenseNumber',
            'insurancePolicyNumber',
            'insurancePolicyIssuedDate'
        ];

        // If no formData (API returned "not found"), treat as 0%
        const isEmpty = !formData || Object.values(formData).every(
            val => val === '' || val === null || val === undefined
        );

        if (isEmpty) {
            setCompletion(0);
            if (typeof onFormCompletionChange === 'function') {
                onFormCompletionChange(0);
            }
            return;
        }

        const filledCount = allFields.filter(field => {
            const val = formData[field];
            return val !== '' && val !== null && val !== undefined;
        }).length;

        const percent = Math.round((filledCount / allFields.length) * 100);
        setCompletion(percent);

        if (typeof onFormCompletionChange === 'function') {
            onFormCompletionChange(percent);
        }
    }, [formData, onFormCompletionChange]);

    useEffect(() => {
        calculateProgress();
    }, [calculateProgress]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Simple sanitization: Uppercase PAN and IFSC codes
        const formattedValue = (name === 'panNumber' || name === 'ifscCode') ? value.toUpperCase() : value;

        setFormData(prevData => ({
            ...prevData,
            [name]: formattedValue
        }));
    };

    const handleBankSelectChange = (e) => {
        const { name, value } = e.target;

        setFormData(prevData => ({
            ...prevData,
            [name]: value,
            // Clear otherBankName if a non-'Other' bank is selected
            otherBankName: value !== 'Other' ? '' : prevData.otherBankName
        }));
    };

    const handleSaveChanges = async () => {
        // Validation Check 
        if (
            !formData.panNumber ||
            !formData.accountNumber ||
            !formData.ifscCode ||
            !formData.accountHolderName
        ) {
            setSnackbar({
                open: true,
                message: 'Required Fields are missing',
                severity: 'error'
            })
            return false;
        }

        // Validation for 'Other' bank name
        if (formData.bankName === 'Other' && !formData.otherBankName.trim()) {
            toast.error("Validation Error: Please enter the Bank Name in the 'Other Bank Name' field.");
            return false;
        }

        // Prepare data for API in the expected backend format
        const apiPayload = {
            user_id: userId,
            bankingDetails: {
                account_holder_name: formData.accountHolderName,
                bank_name: formData.bankName === 'Other' ? formData.otherBankName : formData.bankName,
                branch_name: formData.branchName,
                account_number: formData.accountNumber,
                account_type: formData.accountType,
                ifsc_code: formData.ifscCode,
            },
            statutoryDetails: {
                pan_number: formData.panNumber,
                aadhaar_number: formData.aadhaarNumber,
                uan_number: formData.uanNumber,
                esic_number: formData.esicNumber,
                esic_enrollment_date: formData.esiEnrolledDate ? formData.esiEnrolledDate : null,
                pf_enrollment_date: formData.pfStartDate ? formData.pfStartDate : null,
                pf_number : formData.pfNumber ? formData.pfNumber : null,
                gratuity_number :formData.gratuityNumber ? formData.gratuityNumber : null,
                passport_number: formData.passportNumber,
                driving_license_number: formData.drivingLicenseNumber,
                insurance_policy_number: formData.insurancePolicyNumber,
                policy_issued_date: formData.insurancePolicyIssuedDate ? formData.insurancePolicyIssuedDate : null,
            }
        };


        setLoading(true);

        try {
            const response = await axiosInstance(localAxiosPut('/hr/updateEmployeeFinanceAndStatutory', apiPayload, Token))


            if (response.data.status === 'SUCCESS') {
                setSnackbar({
                    open: true,
                    message: response.data.message || 'Banking & Statutory Details Saved/Updated Successfully!',
                    severity: 'success'
                });
                onUpdateEmployeeSuccess();
                if (navigationContext?.returnToRehire && onSaveAndReturn) {
                    onSaveAndReturn();
                }
                return true;
            } else {
                setSnackbar({
                    open: true,
                    message: response.data.message || 'Failed to update the details',
                    severity: 'error'
                });
                return false;
            }
        } catch (error) {
            console.error('API Save Error:', error);

            const apiError = error?.response?.data;
            let userFriendlyMessage = 'An unexpected error occurred.';

            if (apiError?.message?.error === 'Validation error' || apiError?.message?.msg === 'Internal Server Error') {
                userFriendlyMessage = apiError?.message?.error || 'Please check your form — Bank Account number, Aadhar and  Pan Numbers must be unique.';
            } else if (apiError?.message?.msg) {
                userFriendlyMessage = apiError.message.msg;
            } else if (error.message?.includes('Network Error')) {
                userFriendlyMessage = 'Network error — please check your connection.';
            }

            setSnackbar({
                open: true,
                message: userFriendlyMessage,
                severity: 'error'
            });
            return false
        } finally {
            setLoading(false);
        }
    };

    const isOtherBankSelected = formData.bankName === 'Other';

    return (
        <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>

                {/* --- 1. BANKING DETAILS SECTION --- */}
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <SubHeading>
                            Banking Details
                        </SubHeading>
                        <Box sx={{ width: 220, textAlign: 'right' }}>
                            <Typography variant="body2" fontWeight={600}>
                                {`Form Completion: ${completion}%`}
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={completion}
                                sx={{
                                    height: 8,
                                    borderRadius: 5,
                                    mt: 0.5,
                                    backgroundColor: '#e0e0e0',
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: completion === 100 ? '#2ecc71' : '#1976d2'
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                </Grid>

                {/* Account Holder Name */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        required
                        label="Account Holder Name (as per bank records)"
                        name="accountHolderName"
                        type="text"
                        value={formData.accountHolderName}
                        onChange={handleInputChange}
                        helperText="Must match the name on your bank records."
                        size='medium'
                        disabled={loading}
                    />
                </Grid>

                {/* Account Number */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        required
                        label="Account Number"
                        name="accountNumber"
                        type="text"
                        inputProps={{ inputMode: 'numeric' }}
                        value={formData.accountNumber}
                        onChange={handleInputChange}
                        helperText="Ensure the account number is accurate for salary processing."
                        size='medium'
                        disabled={loading}
                    />
                </Grid>

                {/* Bank Name Dropdown */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth requiredsize='medium'>
                        <InputLabel>Bank Name</InputLabel>
                        <Select
                            label="Bank Name"
                            name="bankName"
                            value={formData.bankName}
                            onChange={handleBankSelectChange}
                            disabled={loading}
                        >
                            {mockBanks.map(bank => (
                                <MenuItem key={bank} value={bank}>{bank}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Branch Name */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Branch Name"
                        name="branchName"
                        type="text"
                        value={formData.branchName}
                        onChange={handleInputChange}
                        size='medium'
                        disabled={loading}
                    />
                </Grid>

                {/* Conditional Other Bank Name Field */}
                {isOtherBankSelected && (
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            required
                            label="Other Bank Name"
                            name="otherBankName"
                            type="text"
                            value={formData.otherBankName}
                            onChange={handleInputChange}
                            helperText="Please enter the exact name of the bank."
                            size='medium'
                            disabled={loading}
                        />
                    </Grid>
                )}
                <Grid item xs={12} sm={isOtherBankSelected ? 6 : 0} />

                {/* Account Type */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth requiredsize='medium'>
                        <InputLabel>Account Type</InputLabel>
                        <Select
                            label="Account Type"
                            name="accountType"
                            value={formData.accountType}
                            onChange={handleInputChange}
                            disabled={loading}
                        >
                            {accountTypes.map(type => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* IFSC Code */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        required
                        label="IFSC Code"
                        name="ifscCode"
                        type="text"
                        value={formData.ifscCode}
                        onChange={handleInputChange}
                        helperText="11-digit alphanumeric code."
                        size='medium'
                        disabled={loading}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                </Grid>

                {/* --- 2. STATUTORY COMPLIANCE SECTION --- */}
                <Grid item xs={12}>
                    <SubHeading>
                        Statutory Compliance (India)
                    </SubHeading>
                </Grid>

                {/* PAN Number */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        required
                        label="PAN Number"
                        name="panNumber"
                        type="text"
                        inputProps={{ maxLength: 10 }}
                        value={formData.panNumber}
                        onChange={handleInputChange}
                        helperText="Permanent Account Number (10 characters, mandatory)."
                        size='medium'
                        disabled={loading}
                    />
                </Grid>

                {/* Aadhaar Number */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        required
                        label="Aadhaar Number"
                        name="aadhaarNumber"
                        type="text"
                        inputProps={{ inputMode: 'numeric', maxLength: 12 }}
                        value={formData.aadhaarNumber}
                        onChange={handleInputChange}
                        helperText="12-digit unique identification number (Optional)."
                        size='medium'
                        disabled={loading}
                    />
                </Grid>

                {/* UAN Number */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="UAN (Universal Account Number)"
                        name="uanNumber"
                        type="text"
                        inputProps={{ inputMode: 'numeric', maxLength: 12 }}
                        value={formData.uanNumber}
                        onChange={handleInputChange}
                        helperText="Mandatory for PF compliance."
                        size='medium'
                        disabled={loading}
                    />
                </Grid>
                 <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Gratuity Number"
                        name="gratuityNumber"
                        type="text"                        
                        value={formData.gratuityNumber}
                        onChange={handleInputChange}                       
                        size='medium'
                        disabled={loading}
                    />
                </Grid>

                 <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="PF Number"
                        name="pfNumber"
                        type="text"                   
                        value={formData.pfNumber}
                        onChange={handleInputChange}                       
                        size='medium'
                        disabled={loading}
                    />
                </Grid>


                {/* PF Start Date */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="PF Enrolment Date"
                        name="pfStartDate"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={formData.pfStartDate}
                        onChange={handleInputChange}
                        helperText="The date when PF contributions began."
                        size='medium'
                        disabled={loading}
                    />
                </Grid>

                {/* ESIC Number */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="ESIC Number"
                        name="esicNumber"
                        type="text"
                        value={formData.esicNumber}
                        onChange={handleInputChange}
                        helperText="Required for ESI contribution."
                        size='medium'
                        disabled={loading}
                    />
                </Grid>

                {/* ESI Enrolment Date */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="ESI Enrolment Date"
                        name="esiEnrolledDate"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={formData.esiEnrolledDate}
                        onChange={handleInputChange}
                        helperText="The date when ESI contributions began."
                        size='medium'
                        disabled={loading}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Insurance Policy Number"
                        name="insurancePolicyNumber"
                        type="text"
                        value={formData.insurancePolicyNumber}
                        onChange={handleInputChange}
                        size='medium'
                        disabled={loading}
                    />
                </Grid>

                {/* Insurance Policy Issued Date */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Policy Issued Date"
                        name="insurancePolicyIssuedDate"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={formData.insurancePolicyIssuedDate}
                        onChange={handleInputChange}
                        size='medium'
                        disabled={loading}
                    />
                </Grid>

                {/* Passport Number */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Passport Number"
                        name="passportNumber"
                        type="text"
                        value={formData.passportNumber}
                        onChange={handleInputChange}
                        size='medium'
                        disabled={loading}
                    />
                </Grid>

                {/* Driving License Number */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Driving License Number"
                        name="drivingLicenseNumber"
                        type="text"
                        value={formData.drivingLicenseNumber}
                        onChange={handleInputChange}
                        size='medium'
                        disabled={loading}
                    />
                </Grid>
            </Grid>

            {/* Save Changes Button at the bottom */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                {navigationContext?.returnToRehire ? (
                    <Paper
                        elevation={3}
                        sx={{
                            width: '100%',
                            p: 2,
                            bgcolor: 'info.light',
                            borderLeft: '4px solid',
                            borderColor: 'info.main'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Rehire Verification in Progress
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Completing verification for: <strong>{navigationContext.stepLabel || 'Banking & Statutory Details'}</strong>
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                color="info"
                                onClick={async () => {
                                    const success = await handleSaveChanges();
                                    if (success && onSaveAndReturn) {
                                        onSaveAndReturn();
                                    }
                                }}
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Verify & Return to Rehire'}
                            </Button>
                        </Box>
                    </Paper>
                ) : (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveChanges}
                        sx={{ py: 1.5, px: 4 }}
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : 'Save Details'}
                    </Button>
                )}
            </Box>

            <ScrollToTop />
            <Modal
                open={openTokenExpirePopup}
                onClose={handleCloseTokenExpirePopup}
                aria-labelledby="token-expire-modal-title"
                aria-describedby="token-expire-modal-description"
            >
                <TokenExpirePopup onClose={handleCloseTokenExpirePopup} />
            </Modal>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BankingStatutory;